import { z } from "zod";
import { optionalIco, optionalUuid } from "../lib/zod-helpers";
import { novaFirmaInlineSchema } from "./hca-firmy";
import { paginationSchema, sortSchema } from "./pagination";

/**
 * HCA Moravia — validační schémata pro `hca.prihlasky` tRPC router.
 *
 * Stav platby a stav účasti jsou samostatné osy (plán §S.2.3).
 * Sleva > 0 vyžaduje `slevaDuvod` (validace přes `.refine`) a backend
 * navíc vynucuje permission `hca.prihlasky.sleva` (admin-only).
 */

export const stavPlatbyEnum = z.enum([
  "NEZAPLACENO",
  "NEUHRAZENO",
  "CASTECNE",
  "UHRAZENO",
  "STORNOVANO",
]);
export type StavPlatby = z.infer<typeof stavPlatbyEnum>;

export const stavUcastiEnum = z.enum(["PRIHLASEN", "ZUCASTNIL", "NEZUCASTNIL", "ZRUSENO"]);
export type StavUcasti = z.infer<typeof stavUcastiEnum>;

export const prihlaskaSourceEnum = z.enum(["WEB", "TABLET", "OBCHODNIK", "IMPORT"]);
export type PrihlaskaSource = z.infer<typeof prihlaskaSourceEnum>;

/**
 * Base64 PNG podpis z canvas SignaturePad komponenty (Sprint 45, Jirka merge).
 * Frontend posílá RAW base64 PNG bez `data:image/png;base64,` prefixu, proto
 * validujeme PNG magic (`iVBORw0KGg…`) + base64 charset — brání uložení
 * libovolného textu na veřejném endpointu (security review Codex). Limit
 * ~150 kB bohatě stačí na 500×200 podpisový canvas; min 100 chrání před
 * prázdným submitem.
 */
export const podpisBase64Schema = z
  .string()
  .min(100, "Podpis je prázdný — podepište se prosím")
  .max(200_000, "Podpis je příliš velký")
  .regex(/^iVBORw0KGg[A-Za-z0-9+/=]+$/, "Neplatný formát podpisu");

/**
 * Vytvoření přihlášky (admin / obchodnik path).
 * Cena je volitelná — backend ji doplní z `hca_products.cenaKatalog`
 * pokud není zadaná. Sleva > 0 vyžaduje důvod.
 */
export const createPrihlaskaSchema = z
  .object({
    klientId: z.string().uuid("Neplatné ID klienta"),
    terminId: z.string().uuid("Neplatné ID termínu"),
    // Prázdný `type="number"` input dorazí jako "" — bez tohoto preprocessu by
    // `z.coerce.number()` shodil "" na 0 (ne undefined), což na serveru potlačí
    // auto-dopočet ceny z katalogu produktu → přihláška za 0 Kč. "" / null tedy
    // mapujeme na undefined; 0 zadaná explicitně zůstává platná cena.
    cena: z.preprocess(
      (v) => (v === "" || v === null ? undefined : v),
      z.coerce
        .number()
        .nonnegative("Cena nesmí být záporná")
        .max(10_000_000, "Cena mimo povolený rozsah")
        .optional(),
    ),
    slevaPct: z.coerce
      .number()
      .min(0, "Sleva nesmí být záporná")
      .max(50, "Sleva nesmí přesáhnout 50 %")
      .optional(),
    slevaDuvod: z.string().max(500, "Důvod slevy musí být 500 znaků nebo méně").optional(),
    source: prihlaskaSourceEnum.default("OBCHODNIK"),
    poznamka: z.string().max(5000, "Poznámka musí být 5000 znaků nebo méně").optional(),
    gdprConsent: z.literal(true, {
      errorMap: () => ({ message: "GDPR souhlas je povinný" }),
    }),
    /** Volitelný podpis — admin path obvykle není nutný (Sprint 45). */
    podpisBase64: podpisBase64Schema.optional(),
    /**
     * Spustit e-mailové série s triggerem `PO_PRIHLASCE` (např. potvrzení
     * registrace) i pro ručně zadanou přihlášku. Server default `false` =
     * zpětná kompatibilita pro ostatní volající (importy, skripty); formuláře
     * hodnotu posílají vždy explicitně a ve výchozím stavu je zaškrtnuto.
     * Vypíná se u přihlášek dopisovaných zpětně.
     */
    odeslatAutoEmaily: z.boolean().default(false),
  })
  .refine(
    (data) =>
      !data.slevaPct || data.slevaPct === 0 || (data.slevaDuvod && data.slevaDuvod.length > 0),
    { message: "Pokud je sleva > 0, je nutné zadat důvod", path: ["slevaDuvod"] },
  );

/**
 * Rychlé přidání účastníka na termín — admin zadá jméno/e-mail/telefon,
 * server najde/vytvoří klienta a přihlášku jedním krokem.
 *
 * Volitelný blok firmy (zpětná vazba majitele — „nahodím firmu s IČO a pod
 * ní rovnou kontakt + přihlášku"): buď `firmaId` (existující firma), nebo
 * `novaFirma` (server dedupuje dle IČO a normalizovaného názvu, jinak
 * založí). Obojí zároveň je chyba — refine níže.
 */
export const quickAddPrihlaskaSchema = z
  .object({
    terminId: z.string().uuid("Neplatné ID termínu"),
    jmeno: z
      .string()
      .trim()
      .min(1, "Jméno je povinné")
      .max(255, "Jméno musí být 255 znaků nebo méně"),
    email: z
      .string()
      .trim()
      .toLowerCase()
      .min(1, "E-mail je povinný")
      .email("Neplatný e-mail")
      .max(255, "E-mail musí být 255 znaků nebo méně"),
    telefon: z.string().trim().max(32, "Telefon musí být 32 znaků nebo méně").optional(),
    /** Pracovní pozice kontaktu ve firmě (sloupec `hca_klienti.pozice`). */
    pozice: z.string().trim().max(100, "Pozice musí být 100 znaků nebo méně").optional(),
    /** Existující firma, ke které se klient přiřadí. */
    firmaId: optionalUuid("Neplatné ID firmy"),
    /** Nová firma zadaná přímo v přihlášce (dedupe řeší server). */
    novaFirma: novaFirmaInlineSchema.optional(),
    /**
     * GDPR souhlas je povinný i u walk-in přidání — `gdprConsentAt` je v DB
     * NOT NULL a bez reálného souhlasu se přihláška nesmí založit vůbec
     * (stejný vzor jako `gdprConsent` v `createPrihlaskaSchema`).
     */
    gdprSouhlas: z.literal(true, {
      errorMap: () => ({ message: "GDPR souhlas je povinný" }),
    }),
    /**
     * Spustit e-mailové série s triggerem `PO_PRIHLASCE` (např. potvrzení
     * registrace) i pro ručně zadanou přihlášku. Server default `false` =
     * zpětná kompatibilita pro ostatní volající (importy, skripty); formuláře
     * hodnotu posílají vždy explicitně a ve výchozím stavu je zaškrtnuto.
     * Vypíná se u přihlášek dopisovaných zpětně.
     */
    odeslatAutoEmaily: z.boolean().default(false),
  })
  .refine((data) => !(data.firmaId && data.novaFirma), {
    message: "Zadejte buď existující firmu, nebo novou — ne obojí",
    path: ["novaFirma"],
  });

export type QuickAddPrihlaskaInput = z.infer<typeof quickAddPrihlaskaSchema>;

/** Inline editace stavu platby (P0 blocker per §S.4). */
export const updateStavPlatbySchema = z.object({
  id: z.string().uuid("Neplatné ID přihlášky"),
  stavPlatby: stavPlatbyEnum,
  poznamka: z.string().max(2000, "Poznámka musí být 2000 znaků nebo méně").optional(),
});

/** Inline editace stavu účasti. */
export const updateStavUcastiSchema = z.object({
  id: z.string().uuid("Neplatné ID přihlášky"),
  stavUcasti: stavUcastiEnum,
  poznamka: z.string().max(2000, "Poznámka musí být 2000 znaků nebo méně").optional(),
});

/**
 * Úprava přihlášky po odeslání (zpětná vazba Adély — překlep v ceně či
 * poznámce, dodatečná sleva). Všechna pole kromě `id` jsou volitelná — mění
 * se jen zaslaná. Pravidla ceny a slevy zrcadlí `createPrihlaskaSchema`
 * (cena nonneg ≤ 10M, sleva 0–50 % a > 0 vyžaduje důvod); `null` u
 * `slevaDuvod`/`poznamka` hodnotu maže. Stornovanou přihlášku server odmítne.
 * Kontaktní údaje klienta (jméno, e-mail) se opravují na kartě klienta.
 */
export const updatePrihlaskaSchema = z
  .object({
    id: z.string().uuid("Neplatné ID přihlášky"),
    // Prázdný number input ("") = cenu neměnit — stejný preprocess jako
    // v createPrihlaskaSchema (bez něj by "" spadlo na 0 Kč).
    cena: z.preprocess(
      (v) => (v === "" || v === null ? undefined : v),
      z.coerce
        .number()
        .nonnegative("Cena nesmí být záporná")
        .max(10_000_000, "Cena mimo povolený rozsah")
        .optional(),
    ),
    slevaPct: z.coerce
      .number()
      .min(0, "Sleva nesmí být záporná")
      .max(50, "Sleva nesmí přesáhnout 50 %")
      .optional(),
    slevaDuvod: z
      .string()
      .max(500, "Důvod slevy musí být 500 znaků nebo méně")
      .nullable()
      .optional(),
    poznamka: z.string().max(5000, "Poznámka musí být 5000 znaků nebo méně").nullable().optional(),
  })
  .refine(
    (data) =>
      !data.slevaPct || data.slevaPct === 0 || (data.slevaDuvod && data.slevaDuvod.length > 0),
    { message: "Pokud je sleva > 0, je nutné zadat důvod", path: ["slevaDuvod"] },
  );

/** Zrušit přihlášku — decrementuje kapacitu termínu v transakci. */
export const cancelPrihlaskaSchema = z.object({
  id: z.string().uuid("Neplatné ID přihlášky"),
  reason: z.string().max(2000, "Důvod musí být 2000 znaků nebo méně").optional(),
});

/** Whitelist řazení přihlášek — router mapuje klíče na sloupce/výrazy. */
export const PRIHLASKY_SORT_KEYS = ["vytvoreno", "klient", "terminDatum", "cena"] as const;
export type PrihlaskySortKey = (typeof PRIHLASKY_SORT_KEYS)[number];

/** Admin list s paginací + filtry. */
export const listPrihlaskySchema = paginationSchema
  .merge(sortSchema(PRIHLASKY_SORT_KEYS, "desc"))
  .extend({
    terminId: optionalUuid(),
    klientId: optionalUuid(),
    stavPlatby: stavPlatbyEnum.optional(),
    stavUcasti: stavUcastiEnum.optional(),
    from: z.coerce.date().optional(),
    to: z.coerce.date().optional(),
    /** Fulltext přes jméno/příjmení/e-mail klienta a název produktu. */
    search: z.string().max(255, "Hledaný text musí být 255 znaků nebo méně").optional(),
  });

/** Non-paginated list filtrovaný podle termínu (pro admin docházka prep). */
export const listPrihlaskyByTerminSchema = z.object({
  terminId: z.string().uuid("Neplatné ID termínu"),
});

/** Non-paginated list filtrovaný podle klienta. */
export const listPrihlaskyByKlientSchema = z.object({
  klientId: z.string().uuid("Neplatné ID klienta"),
});

/** ID-only schema. */
export const prihlaskaIdSchema = z.object({
  id: z.string().uuid("Neplatné ID přihlášky"),
});

/**
 * Public submit schema — bez auth, volá z public /registrace/$terminId
 * stránky. Klienta vytváří na fly pokud e-mail ještě neexistuje; jinak
 * reuse existující.
 */
export const publicPrihlaskaSchema = z.object({
  terminId: z.string().uuid("Neplatné ID termínu"),
  email: z.preprocess(
    (val) => (typeof val === "string" ? val.trim().toLowerCase() : val),
    z.string().email("Neplatný e-mail").max(255, "E-mail musí být 255 znaků nebo méně"),
  ),
  jmeno: z.string().min(1, "Jméno je povinné").max(100, "Jméno musí být 100 znaků nebo méně"),
  prijmeni: z
    .string()
    .min(1, "Příjmení je povinné")
    .max(100, "Příjmení musí být 100 znaků nebo méně"),
  telefon: z.string().min(5, "Telefon je povinný").max(30, "Telefon musí být 30 znaků nebo méně"),
  firma: z.string().max(255, "Firma musí být 255 znaků nebo méně").optional(),
  ico: optionalIco(),
  gdprConsent: z.literal(true),
  dietaPozadavek: z.string().max(500, "Dieta musí být 500 znaků nebo méně").optional(),
  poznamka: z.string().max(1000, "Poznámka musí být 1000 znaků nebo méně").optional(),
  /** Cloudflare Turnstile token (server ověří, je-li nakonfigurováno). */
  turnstileToken: z.string().max(4096, "Neplatný token ověření").optional(),
  /** Sprint 45 (Jirka merge) — veřejná registrace může přiložit podpis. */
  podpisBase64: podpisBase64Schema.optional(),
});

/** Ověření confirmation tokenu po úspěšné registraci. */
export const confirmationTokenSchema = z.object({
  token: z.string().min(10, "Token je povinný").max(2000, "Token je příliš dlouhý"),
});

export type CreatePrihlaskaInput = z.infer<typeof createPrihlaskaSchema>;
export type UpdatePrihlaskaInput = z.infer<typeof updatePrihlaskaSchema>;
export type UpdateStavPlatbyInput = z.infer<typeof updateStavPlatbySchema>;
export type UpdateStavUcastiInput = z.infer<typeof updateStavUcastiSchema>;
export type CancelPrihlaskaInput = z.infer<typeof cancelPrihlaskaSchema>;
export type ListPrihlaskyInput = z.infer<typeof listPrihlaskySchema>;
export type ListPrihlaskyByTerminInput = z.infer<typeof listPrihlaskyByTerminSchema>;
export type ListPrihlaskyByKlientInput = z.infer<typeof listPrihlaskyByKlientSchema>;
export type PrihlaskaIdInput = z.infer<typeof prihlaskaIdSchema>;
export type PublicPrihlaskaInput = z.infer<typeof publicPrihlaskaSchema>;
export type ConfirmationTokenInput = z.infer<typeof confirmationTokenSchema>;
